/**
 * DOM 外壳的元素定位与舞台等比缩放（界面布局规范、决策 0018）。
 * 外壳结构由 main.ts 生成；这里只按 data 属性找到各区域，并把画布按世界逻辑尺寸
 * 等比塞进舞台区域——缩放系数 min(可用宽 / 世界宽, 可用高 / 世界高)，居中留黑。
 */
export interface GameShell {
  /** 网格根：面板开合通过 `data-panel` 切换轨道尺寸。 */
  root: HTMLElement;
  /** 舞台网格区域：可用空间由 CSS Grid 决定，画布只在其中等比缩放。 */
  stage: HTMLElement;
  /** 画布与覆盖层的共同容器，尺寸由 fitStage 写入。 */
  frame: HTMLElement;
  canvasHost: HTMLElement;
  /** 右上角按钮组；开发构建把调参面板的开关挂在这里。 */
  controls: HTMLElement;
  /** 面板停靠区域；生产构建为空，轨道恒为 0。 */
  dock: HTMLElement;
}

export function queryShell(root: ParentNode): GameShell {
  const get = (selector: string): HTMLElement => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element === null) throw new Error(`Missing shell element: ${selector}`);
    return element;
  };
  return {
    root: get("[data-shell]"),
    stage: get("[data-stage]"),
    frame: get("[data-stage-frame]"),
    canvasHost: get("#canvas-host"),
    controls: get('[data-hud="controls"]'),
    dock: get("[data-dock]"),
  };
}

/**
 * 观察舞台区域尺寸，把 frame 设为在其中能容纳的最大世界比例矩形。
 * 用 ResizeObserver 而非 window.resize：面板开合改变的是网格轨道，窗口尺寸并不变。
 * 返回停止观察的函数。
 */
export function fitStage(
  stage: HTMLElement,
  frame: HTMLElement,
  worldWidth: number,
  worldHeight: number,
): () => void {
  const apply = (): void => {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    const scale = Math.max(
      0,
      Math.min(width / worldWidth, height / worldHeight),
    );
    // 取整避免亚像素尺寸让画布边缘发虚；按同一系数取整，比例误差不超过 1px。
    frame.style.width = `${Math.floor(worldWidth * scale)}px`;
    frame.style.height = `${Math.floor(worldHeight * scale)}px`;
  };
  apply();
  if (typeof ResizeObserver === "undefined") {
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }
  const observer = new ResizeObserver(apply);
  observer.observe(stage);
  return () => observer.disconnect();
}
